import { createFileRoute } from "@tanstack/react-router";
import ArticlePoolResurfacingCost from "@/pages/ArticlePoolResurfacingCost";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_POOL_RESURFACING_COST } from "@/data/blogData";
import { FAQS_POOL_RESURFACING_COST } from "@/data/poolResurfacingCostData";

const PATH = "/blog/pool-resurfacing-cost-south-florida";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: "How Much Does Pool Resurfacing Cost in South Florida?",
  description:
    "Learn what pool resurfacing may cost in South Florida, which factors change the price, what a quote should include, and how to compare estimates accurately.",
  datePublished: "2026-10-19",
  dateModified: "2026-10-19",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_POOL_RESURFACING_COST) {
  blogPosting.image = FEATURED_IMAGE_POOL_RESURFACING_COST;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "How Much Does Pool Resurfacing Cost in South Florida?",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_POOL_RESURFACING_COST.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_POOL_RESURFACING_COST.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/pool-resurfacing-cost-south-florida",
)({
  head: () =>
    buildHead({
      title: "Pool Resurfacing Cost in South Florida: 2026 Guide",
      description:
        "Learn what pool resurfacing may cost in South Florida, which factors change the price, what a quote should include, and how to compare estimates accurately.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_POOL_RESURFACING_COST || undefined,
      jsonLd,
    }),
  component: ArticlePoolResurfacingCost,
});
